import { IBaseNode } from './IBaseNode';

export class FindResult<T extends IBaseNode> {
    public tree: Tree<T>;
    public indexInParentsChildrenArray: number | null;

    constructor(tree: Tree<T>, indexInParentsChildrenArray: number | null) {
        this.tree = tree;
        this.indexInParentsChildrenArray = indexInParentsChildrenArray;
    }
}

export class Tree<T extends IBaseNode> {
    //unique identifier for finding this tree
    public name: string;
    //some string value defined by the implementor of IBaseNode
    public description: string;
    public data: T;
    public children: Tree<T>[];
    public parent: Tree<T> | null;
    public loading: boolean;
    public loaded: boolean;

    constructor(data: T, parent: Tree<T> | null = null, childrenData: T[] = []) {
        this.name = data.name;
        this.description = data.description;
        this.data = data;
        this.parent = parent;
        this.children = [];
        for (const d of childrenData) {
            this.children.push(new Tree<T>(d, this));
        }
    }

    findByName(name: string): FindResult<T> | null {
        //determine if this tree is the match
        if (this.name === name) {
            if (this.parent === null) {
                //when parent is null, there is no index of this node in parent's children array, return null there
                return new FindResult(this, null);
            } else {
                //when a parent to this node exists, get the index of this node in the parent's children array to pass back
                //to the caller
                return new FindResult(this, this.parent.getIndexOfChildByName(name));
            }
        }
        for (const child of this.children) {
            //recurse through the children until one is found
            const findRes = child.findByName(name);
            if (findRes) {
                return findRes;
            }
        }
        //if not this node and any children node, not present in this tree
        return null;
    }

    getIndexOfChildByName(name: string): number | null {
        for (const [i, child] of this.children.entries()) {
            if (child.name === name) {
                return i;
            }
        }
        return null;
    }

    addChild(data: T) {
        this.children.push(new Tree<T>(data, this));
    }

    addChildTo(parentName: string, data: T): boolean {
        //find a tree in this tree matching the specified name
        const searchRes = this.findByName(parentName);
        if (searchRes) {
            searchRes.tree.addChild(data);
            return true;
        } else {
            return false;
        }
    }

    remove(name: string): boolean {
        //find a tree in this tree matching the specified name
        const searchRes = this.findByName(name);
        if (searchRes && searchRes.indexInParentsChildrenArray !== null) {
            //remove this node from it's parent's children array at the index specified
            //in the return results
            searchRes.tree.parent.children.splice(searchRes.indexInParentsChildrenArray, 1);
            return true;
        } else {
            return false;
        }
    }

    getPathTo(): Tree<T>[] {
        //if this node has no parent, no path to it exists
        if (!this.parent) {
            return [];
        }
        //go over all the parents of the parents to this node, push them to the array
        const returnArr = [];
        let parentCheck = this.parent;
        while (parentCheck) {
            returnArr.push(parentCheck);
            parentCheck = parentCheck.parent;
        }
        //reverse so the array goes Top Level - This Node
        returnArr.reverse();
        return returnArr;
    }
}
